import { createFileRoute } from '@tanstack/react-router';
import { type ReactElement, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '@/api';
import { AlertBox } from '@/common/AlertBox';
import { InfoSectionContainer } from '@/common/InfoSection';
import { LoadingSpinner } from '@/common/LoadingSpinner';
import { TabsBar } from '@/common/TabsBar';
import { TitleWithQuestion } from '@/common/TitleWithQuestion';
import { WBSButton } from '@/common/WBSButton';
import { CheckboxLabel, WBSCheckbox } from '@/common/WBSCheckbox';
import { useAdminTabs } from '@/groups/coordinator/useAdminTabs';
import { BorderedRichTextEditor } from '@/messages/BorderedRichTextEditor';
import { PageContent } from '@/settings/settingsStyles';
import { useRichTextEditor } from '~/hooks/useRichTextEditor';
import { GroupHeaderWrapper } from '~/styles/layoutElements';

export const Route = createFileRoute(
  '/_app/_dashboard/groups/$groupName/admin/student-notice-msg',
)({
  component: StudentNoticeMsgScreen,
});

function StudentNoticeMsgScreen(): ReactElement {
  const { t } = useTranslation();
  const { groupName } = Route.useParams();
  const adminTabs = useAdminTabs();
  // Seeded from the stored row by the latch below, never from `true`: 3 of the
  // 17 migrated rows are disabled and a `true` default renders them mis-checked
  // — one save away from publishing an announcement the helper switched off.
  const [displayAnnouncement, setDisplayAnnouncement] = useState(false);
  const [saved, setSaved] = useState(false);

  const editor = useRichTextEditor();
  const { getEditorContent, getEditorHtml, setEditorHtml } = editor;

  // The slug -> id hop, as every group tab does it. `enabled` cannot guard the
  // parameters object — qraft evaluates `parameters` before it reads the
  // options — so the house sentinel (0, never a real id) stands in until the
  // slug resolves.
  const { data: group, isError: groupFailed } = api.getGroupBySlug.useQuery({
    path: { group_slug: groupName },
  });
  const groupId = group?.id ?? 0;

  // The slug read is NOT the refusal signal here. `require_group_readable`
  // returns `None` rather than raising on a PUBLIC group
  // (`modules/groups/service.py:138-155`), and every study group created since
  // go-live is PUBLIC (`helper_applications/service.py:101-113` omits
  // `status`), so a non-member's slug read 200s and the 403 arrives on this
  // query instead.
  const { data: announcement, isError: announcementFailed } =
    api.getGroupAnnouncement.useQuery(
      { path: { group_id: groupId } },
      { enabled: groupId > 0 },
    );

  const {
    mutateAsync: saveAnnouncement,
    isPending: saving,
    error: saveFailed,
  } = api.setGroupAnnouncement.useMutation();

  // Latched on the record's own group id rather than a bare boolean: the seed
  // must not re-run when the post-save invalidation refetches (it would discard
  // whatever the helper has typed since), but it must run again if this
  // component is reused for a different group.
  const seededGroupId = useRef<number | null>(null);
  useEffect(() => {
    if (!announcement) return;
    if (seededGroupId.current === announcement.group_id) return;
    seededGroupId.current = announcement.group_id;
    setEditorHtml(announcement.content);
    setDisplayAnnouncement(announcement.is_enabled);
  }, [announcement, setEditorHtml]);

  const handleSave = async (): Promise<void> => {
    // The button is disabled while the write is in flight, but Shift+Enter
    // reaches this handler around it (`useRichTextEditor.ts:95-99` →
    // `BorderedRichTextEditor`'s `onSubmit`). The write has no idempotency key
    // and duplicate content is valid, so a second call would store the row
    // twice and fan the student notifications out twice.
    if (saving) return;
    setSaved(false);
    try {
      await saveAnnouncement({
        path: { group_id: groupId },
        // `getEditorHtml`, not `getEditorContent`: the stored format is HTML
        // and `innerText` would drop every format the toolbar applied. The
        // plain-text read still decides EMPTINESS, because the contenteditable
        // seeds itself with `<div><br></div>` on first focus
        // (`RichTextEditor.tsx:29-31`) and only clears that on blur — a save
        // driven by Shift+Enter would otherwise store a blank line where legacy
        // stores the empty string.
        body: {
          content: getEditorContent() ? getEditorHtml() : '',
          is_enabled: displayAnnouncement,
        },
      });
      setSaved(true);
      await api.getGroupAnnouncement.invalidateQueries();
    } catch {
      // Rendered from `saveFailed` below.
    }
  };

  // A refused READ has no screen (W2.8 D14). `useAdminTabs.ts:60-75` offers
  // Student Announcement to every non-platform-admin with no membership check,
  // and the slug read admits anyone on a PUBLIC group, so without this arm a
  // non-member helper and a platform administrator both get a fully rendered
  // authoring form over a group they cannot read and whose save 403s. Legacy
  // answers the non-member `<title>Page not found</title>`
  // (`WBS_Group_Extension.php:146-179`).
  //
  // Each arm is paired with "and we hold nothing": query-core sets
  // `status: "error"` on any error action whatever data the query already
  // carries, so a failed post-write refetch is an `isError` on a query whose
  // record is still on screen — and the unguarded form would blank out content
  // the helper is in the middle of editing.
  const refused =
    (groupFailed && group === undefined) ||
    (announcementFailed && announcement === undefined);
  const pending =
    !refused && (group === undefined || announcement === undefined);

  return (
    <PageContent>
      <TabsBar tabs={adminTabs} base="/groups/$groupName/admin" />
      <GroupHeaderWrapper>
        <TitleWithQuestion title={t('lessons.student-announcement')} />
      </GroupHeaderWrapper>
      {refused ? (
        <AlertBox variant="error">{t('wbs.page-not-found')}</AlertBox>
      ) : pending ? (
        // Never a blank form: the latched effect is about to write the stored
        // announcement into an editor that has already painted empty.
        <LoadingSpinner centered />
      ) : (
        <InfoSectionContainer>
          {saved && (
            <AlertBox variant="success">
              {t('lessons.announcement-updated-successfully')}
            </AlertBox>
          )}
          {saveFailed && (
            // A failed WRITE keeps its screen, unlike a refused read: the
            // editor still holds content that exists nowhere else.
            <AlertBox variant="error">
              {t('wbs.something-went-wrong-try')}
            </AlertBox>
          )}
          <CheckboxRow>
            <WBSCheckbox
              id="display-announcement"
              checked={displayAnnouncement}
              onChange={(e) => setDisplayAnnouncement(e.target.checked)}
            />
            <CheckboxLabel htmlFor="display-announcement">
              {t('wbs.display-announcement')}
            </CheckboxLabel>
          </CheckboxRow>
          {/* Not disabled when the announcement is switched off — legacy's
              textarea is never disabled, and it is how a helper edits a draft
              before enabling it (measured on groups 418 and 620, both
              `announcement_status=0` with editable content). */}
          <BorderedRichTextEditor
            editor={editor}
            id="announcement-editor"
            visibleTools={['bold', 'italic', 'underline', 'blockquote']}
            maxHeight="400px"
            placeholder={t('lessons.write-an-announcement')}
            onSubmit={() => {
              void handleSave();
            }}
          />
          <SaveRow>
            <WBSButton
              wide
              type="button"
              isLoading={saving}
              disabled={saving}
              onClick={() => {
                void handleSave();
              }}
            >
              {t('actions.save-changes')}
            </WBSButton>
          </SaveRow>
        </InfoSectionContainer>
      )}
    </PageContent>
  );
}

const CheckboxRow = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
`;

const SaveRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
`;
